import { useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { Channel } from '../../../types/assignmentGroups'

export const useOrgChannels = (orgId?: string) => {
  const { auth } = useAuth()
  const [channels, setChannels] = useState<Channel[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  const fetchChannels = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const token = getToken()
      const response = await axios.get('/api/chat/channels', {
        params: { scope: 'organization', organization_id: orgId },
        headers: { Authorization: `Bearer ${token}` },
      })
      setChannels(response.data.items || [])
    } catch (error) {
      console.error('Failed to fetch channels:', error)
      setChannels([])
    } finally {
      setLoading(false)
    }
  }, [orgId, getToken])

  const saveChannel = useCallback(
    async (data: Partial<Channel>) => {
      setSaving(true)
      try {
        const token = getToken()
        const payload = {
          scope: 'organization',
          organization_id: orgId,
          ...data,
        }

        let response
        if (data.id) {
          // Update existing
          response = await axios.post(`/api/chat/channels/${data.id}/`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          })
        } else {
          // Create new
          response = await axios.post('/api/chat/channels', payload, {
            headers: { Authorization: `Bearer ${token}` },
          })
        }

        await fetchChannels()
        return response.data as Channel
      } catch (error) {
        console.error('Failed to save channel:', error)
        throw error
      } finally {
        setSaving(false)
      }
    },
    [orgId, getToken, fetchChannels]
  )

  const deleteChannel = useCallback(
    async (channelId: number) => {
      setSaving(true)
      try {
        const token = getToken()
        await axios.delete(`/api/chat/channels/${channelId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        await fetchChannels()
        return true
      } catch (error) {
        console.error('Failed to delete channel:', error)
        return false
      } finally {
        setSaving(false)
      }
    },
    [getToken, fetchChannels]
  )

  useEffect(() => {
    if (orgId) {
      fetchChannels()
    }
  }, [orgId, fetchChannels])

  return {
    channels,
    loading,
    saving,
    fetchChannels,
    saveChannel,
    deleteChannel,
  }
}
